import { useEffect, useMemo, useRef, useState } from 'react';

import { DEBOUNCE_MS } from '@/components/features/UsersList/constants';
import { BaseMultiAutocompleteProps } from '@/components/shared/Autocomplete';
import BaseMultiAutocomplete from '@/components/shared/Autocomplete/BaseMultiAutocomplete';
import { useAccountUsersAutocomplete } from '@/hooks/autocomplete/useAccountUsersAutocomplete';
import { useOrganizationAccountUsersRelation } from '@/hooks/useOrganizationAccountUsersRelation';
import type { components } from '@/shared/api/openapi';

type AccountUser = components['schemas']['GetAccountUsersAutocompleteEndpoint_GetUsersResponse'];

export type EnhancedAccountUser = AccountUser & {
  isAssignedToUser: boolean;
};

const getOptionLabel = (option: EnhancedAccountUser) => option.email || '';

export interface AccountUserAutocompleteForOrganizationProps {
  organizationId: number;
  onApply?: NonNullable<BaseMultiAutocompleteProps<EnhancedAccountUser>['actionButton']>['onApply'];
  disabled?: boolean;
}

function AccountUserAutocompleteForOrganization({ organizationId, onApply, disabled }: AccountUserAutocompleteForOrganizationProps) {
  const [value, setValue] = useState<EnhancedAccountUser[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const addButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data: users, isLoading: isLoadingUsers } = useAccountUsersAutocomplete({
    filter: debouncedValue,
    enabled: !disabled && debouncedValue.length >= 3,
  });

  const userEmails = useMemo(() => users?.map((u) => u.email).filter((email): email is string => email !== undefined) || [], [users]);

  const {
    data: membershipData,
    isLoading: isLoadingMembership,
    isRefetching: isMembershipRefetching,
  } = useOrganizationAccountUsersRelation({
    organizationId,
    userEmails,
    enabled: !disabled && organizationId > 0 && userEmails.length > 0,
  });

  const enhancedOptions = useMemo<EnhancedAccountUser[]>(() => {
    if (!users) return [];
    if (isLoadingMembership || isMembershipRefetching) return [];
    if (!membershipData)
      return users.map((user) => ({
        ...user,
        isAssignedToUser: false,
      }));

    const membershipMap = new Map(membershipData.map((m) => [m.userEmail, m]));

    return users.map((user) => {
      const membership = membershipMap.get(user.email || '');
      return {
        ...user,
        isAssignedToUser: membership?.isMember || false,
      };
    });
  }, [users, membershipData, isLoadingMembership, isMembershipRefetching]);

  const handleInputChange = (_: React.SyntheticEvent, input: string) => {
    setInputValue(input);
  };

  const handleApply = (selectedUsers: EnhancedAccountUser[]) => {
    onApply?.(selectedUsers);
    setValue([]);
  };

  const getOptionDisabled = (optionLabel: EnhancedAccountUser) => {
    if (isLoadingMembership || isMembershipRefetching || !membershipData) {
      return false;
    }

    const isSelectedOptionDisabled = value.some((val) => val.email === optionLabel.email);

    const option = enhancedOptions.find((u) => u.email === optionLabel.email);
    return option?.isAssignedToUser || isSelectedOptionDisabled;
  };

  const getOptionKey = (option: EnhancedAccountUser) => option.id ?? `key-${option.email}`;

  return (
    <BaseMultiAutocomplete<EnhancedAccountUser>
      autocomplete={{
        options: enhancedOptions,
        value: value,
        loading: isLoadingUsers || isLoadingMembership || isMembershipRefetching,
        onChange: (_, newValue) => setValue(newValue),
        onInputChange: handleInputChange,
        getOptionLabel: getOptionLabel,
        getOptionDisabled: getOptionDisabled,
        getOptionKey: getOptionKey,
        label: 'Search Account Admin to Add',
        placeholder: 'Enter at least 3 characters to search',
        disabled: disabled ?? false,
      }}
      actionButton={{
        onApply: handleApply,
        buttonLabel: 'Add',
        buttonRef: addButtonRef,
      }}
    />
  );
}

export default AccountUserAutocompleteForOrganization;
